import { useState } from 'react'
import { CircleCheck } from 'lucide-react'
import { PhotoType } from '@numeo/types-core'
import { Button } from '@/components/ds2'
import { RevisionCard } from '../uploads/RevisionCard'
import { UploadButton } from '../uploads/UploadButton'
import type { RevisionChoice } from '../uploads/uploadFlow'
import { DONE_LABEL, invoiceMessage, reviewPodMessage, type RowAction } from './rowActions'
import { SetRateAction } from './SetRateAction'
import { useRowRevision } from './useRowRevision'

interface LoadFixActionsProps {
    loadNumber: string
    /** The load's id when a card named it, so an upload goes to exactly this load. */
    loadId?: string
    actions: RowAction[]
    onSend: (message: string) => void
}

/** An action the user did here, in its place. */
const Done = ({ label }: { label: string }) => (
    <span role="status" className="flex items-center gap-1 py-1 text-xs text-success">
        <CircleCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {label}
    </span>
)

/**
 * The actions that fix one load, right in the chat (never a board link: an upload made there would not reach the chat): upload its
 * rate con or POD, set its rate, review its POD, invoice it. The same wherever a load shows (a Ready item, a load table, an invoice
 * row, an invoice refusal). An action the user did shows as done in its place; a rate con that opens a revision of the load is
 * reviewed here, with the board's choices, in place of the actions.
 */
export const LoadFixActions = ({ loadNumber, loadId, actions, onSend }: LoadFixActionsProps) => {
    const rowRevision = useRowRevision()
    const [done, setDone] = useState<ReadonlySet<RowAction['kind']>>(new Set())
    const markDone = (kind: RowAction['kind']) => setDone((known) => new Set(known).add(kind))
    const send = (kind: RowAction['kind'], message: string) => {
        onSend(message)
        markDone(kind)
    }
    // A revision the user accepted or kept ends the upload; one already resolved elsewhere or left pending does not.
    const resolveRevision = (choice: RevisionChoice) => {
        rowRevision.resolve(choice)
        if (choice.kind === 'accepted' || choice.kind === 'kept') markDone('upload_ratecon')
    }

    if (rowRevision.revision)
        return (
            <div className="rounded-xl bg-default p-3">
                <RevisionCard revision={rowRevision.revision} onResolved={resolveRevision} onSubmittingChange={rowRevision.onSubmittingChange} />
            </div>
        )
    if (!actions.length) return null

    const action = (step: RowAction) => {
        if (done.has(step.kind)) return <Done label={DONE_LABEL[step.kind]} />
        switch (step.kind) {
            case 'upload_ratecon':
                return (
                    <UploadButton
                        loadNumber={loadNumber}
                        loadId={loadId}
                        type={PhotoType.RATECON}
                        label={step.label}
                        onRevision={rowRevision.open}
                        onUploaded={() => markDone('upload_ratecon')}
                    />
                )
            case 'upload_pod':
                return (
                    <UploadButton loadNumber={loadNumber} loadId={loadId} type={PhotoType.POD} label={step.label} onUploaded={() => markDone('upload_pod')} />
                )
            case 'set_rate':
                return <SetRateAction loadNumber={loadNumber} label={step.label} onSend={(message) => send('set_rate', message)} />
            case 'review_pod':
                return (
                    <Button size="sm" variant="secondary" onClick={() => send('review_pod', reviewPodMessage(loadNumber))}>
                        {step.label}
                    </Button>
                )
            case 'invoice':
                return (
                    <Button size="sm" onClick={() => send('invoice', invoiceMessage(loadNumber))}>
                        {step.label}
                    </Button>
                )
        }
    }

    return (
        <div className="flex flex-wrap items-start gap-1.5" aria-label={`Fix #${loadNumber}`}>
            {actions.map((step) => (
                <div key={step.kind}>{action(step)}</div>
            ))}
        </div>
    )
}
